<script>
    import { fade } from "svelte/transition";
    import { params } from "@roxi/routify";
    import { untrack, onMount, tick } from "svelte";
    import * as bootstrap from "bootstrap";
    import BreadCrumNav from "../../../../components/breadcrum-nav.svelte";
    import Spinner from "../../../../components/spinner.svelte";
    import Finding from "../../../finding/_components/list.svelte";
    import AssetDetail from "./_components/asset-detail.svelte";
    import Properties from "../../../../components/properties.svelte";
    import AssetRelations from "./_components/asset-relations.svelte";
    import Tag from "../../../../components/tags.svelte";
    import {
        searchAssets,
        getAssetDetail,
        searchProperties,
        searchRelations,
        searchRelatedAssets,
    } from "../../assets.state.svelte.js";
    import tagService from "../../../tag/service.js";
    import vulnerabilitiesApi from "../../../vulnerability/vulnerabilities.api.js";
    import logService from "../../../log/service.js";
    import LogList from "../../../../components/list.svelte";
    import PropNameRow from "../../../../components/powerTable/prop-name-row.svelte";
    import PropValueRow from "../../../../components/powerTable/prop-value-row.svelte";
    import PropLocRow from "../../../../components/powerTable/prop-loc-row.svelte";
    import SeverityTotals from "./_components/severity-totals.svelte";
    import PropKindRow from "../../../../components/powerTable/prop-kind-row.svelte";
    import TagForm from "../../../tag/_form.svelte";
    import { format, locale } from "svelte-i18n";
    import dayjs from "dayjs";
    import { searchFindings } from "../../../finding/findings.state.svelte";
    import powerTableService from "../../../../services/powerTable";
    import { token, user } from "../../../user/user.state.svelte";
    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import "dayjs/locale/es";
    import "dayjs/locale/en";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    let table_is_expanded = $state(false);
    let is_loading = $state(false);
    let is_loading_asset = $state(false);
    let is_loading_props = $state(false);
    let is_loading_rels = $state(false);
    let is_loading_findings = $state(false);
    let is_loading_tags = $state(false);
    let showSettings = $state(false);
    let showTagsForm = $state(false);
    let showTagsDeleteForm = $state(false);
    let refresh_tag_comp = $state(false);
    let loadLogData = $state(false);
    let showProperties = $state(true);
    let props_grouped_by = $state("none");
    let showRelations = $state(true);
    let tagRegexError = $state(null);
    let relationKinds = $state(new Set([]));
    let propsResponse = $state([]);
    let formData = $state({});
    let tags = $state([]);
    let allTags = $state([]);
    let asset = $state({});
    let relatedAssets = $state([]);
    let properties = $state([]);
    let relations = $state([]);
    let findings = $state([]);

    // Modal instances
    let tagModal;
    let logModal;

    const uuidRegex =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i;
    const isValidUUID = (str) => uuidRegex.test(str);

    let propInstructs = $state([
        {
            key: "kind",
            title: $format("list.columns.kind"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropKindRow,
        },
        {
            key: "name",
            title: $format("list.columns.name"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropNameRow,
        },
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "location",
            title: $format("list.columns.location"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropLocRow,
        },
        {
            key: "last_discovery_at",
            title: $format("list.columns.seen"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ]);

    let totals = $derived.by(() => {
        if (asset && asset.finding_count) {
            return {
                critical:
                    (asset.finding_count.critical?.open || 0) +
                    (asset.finding_count.critical?.accepted || 0),
                high:
                    (asset.finding_count.high?.open || 0) +
                    (asset.finding_count.high?.accepted || 0),
                medium:
                    (asset.finding_count.medium?.open || 0) +
                    (asset.finding_count.medium?.accepted || 0),
                low:
                    (asset.finding_count.low?.open || 0) +
                    (asset.finding_count.low?.accepted || 0),
            };
        }
        return { critical: 0, high: 0, medium: 0, low: 0 };
    });

    onMount(() => {
        const bs =
            typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        if (bs) {
            const tagModalEl = document.getElementById("tag-add-new-modal");
            if (tagModalEl) {
                tagModal = new bs.Modal(tagModalEl);
            }
        }
    });

    const openTagModal = (e) => {
        e.preventDefault();
        if (tagModal) tagModal.show();
    };

    const closeTagModal = () => {
        if (tagModal) tagModal.hide();
    };

    const openLogModal = async () => {
        loadLogData = true;
        await tick();
        const bs =
            typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        if (bs) {
            const logModalEl = document.getElementById("get-log-modal");
            if (logModalEl) {
                if (!logModal) {
                    logModal = new bs.Modal(logModalEl);
                    // Clear state when BS finishes hiding modal
                    logModalEl.addEventListener("hidden.bs.modal", () => {
                        loadLogData = false;
                        logModal = null;
                    });
                }
                logModal.show();
            }
        }
    };

    const addNewTag = (event) => {
        closeTagModal();
        return handleTagEdit({ detail: { tag: event.detail.savedTag.id } });
    };

    const handleTagEdit = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === "cancel") return;

        let reqData = {
            tag_id: formData.tag.tag,
            target_object_type: "Asset",
            target_object_id: asset.id,
        };
        tagService
            .saveAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                refresh_tag_comp = !refresh_tag_comp;
                showTagsForm = false;
                await getTags();
            });
    };

    const handleTagDelete = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === "cancel") return;
        let reqData = {
            tag_id: formData.tag.tag,
            target_object_type: "Asset",
            target_object_id: asset.id,
        };
        tagService
            .deleteAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                refresh_tag_comp = !refresh_tag_comp;
                showTagsForm = false;
                showTagsDeleteForm = false;
                await getTags();
            });
    };

    const getAsset = async (asset_id, asset_kind) => {
        if (!token.access_token) return {};
        let assetData;
        if (isValidUUID(asset_id)) {
            assetData = await getAssetDetail(asset_id);
        } else {
            assetData = await searchAssets("v2/asset/", {
                filter: {
                    include_finding_count: true,
                    kind: asset_kind,
                    name: asset_id,
                },
            });
            if (assetData.length < 1) {
                assetData.push({});
            }
            return assetData[0];
        }
        return assetData;
    };

    const getTagsAssociation = async () => {
        return asset.id
            ? await tagService.search("v2/tag/association/", {})
            : [];
    };

    const getAllTags = async (config = { filter: {} }) => {
        allTags = await tagService.search("v2/tag/", config);
        return allTags;
    };

    const getTags = async () => {
        tags = [];
        let associations = await getTagsAssociation();
        allTags = await getAllTags();
        let ids = [];
        for (let association of associations) {
            if (
                association.target_object_type === "Asset" &&
                association.target_object_id === asset.id
            ) {
                ids = [...ids, association.tag_id];
            }
        }
        tags = allTags.filter((tag) => ids.includes(tag.id));
        return {
            tags,
            associations,
            allTags,
        };
    };

    const getRelatedAssets = async (asset_id) => {
        return await searchRelatedAssets(asset_id, {
            filter: { include_finding_count: true },
        });
    };

    const getProperties = async (config = { filter: {} }) => {
        return await searchProperties(config);
    };

    const groupPropertiesByKNV = () => {
        if (props_grouped_by === "knv") return;
        is_loading_props = true;

        let grouped = propsResponse.reduce((a, b) => {
            const found = a.find(
                (e) =>
                    e.name === b.name &&
                    e.value === b.value &&
                    e.kind === b.kind,
            );
            if (found) {
                found.location = found.location + "," + b.location;
            } else {
                a.push({ ...b, location: b.location });
            }
            return a;
        }, []);

        properties = grouped;
        props_grouped_by = "knv";
        propInstructs = propInstructs.filter(
            (col) => col.key !== "last_discovery_at",
        );
        is_loading_props = false;
        showSettings = false;
    };

    const ungroupProperties = () => {
        if (props_grouped_by === "none") return;
        is_loading_props = true;
        properties = propsResponse;
        props_grouped_by = "none";

        propInstructs = [
            ...propInstructs,
            {
                key: "last_discovery_at",
                title: $format("list.columns.seen"),
                filterable: false,
                parseAs: "unsafe-html",
            },
        ];

        is_loading_props = false;
        showSettings = false;
    };

    const getRelations = async (config = { filter: {} }) => {
        let relsResponse = await searchRelations(config);
        for (let relation of relsResponse) {
            relationKinds.add(relation.kind);
        }
        return relsResponse;
    };

    const getFindings = async (config = { filter: {} }) => {
        return await searchFindings("v2/finding/", config);
    };

    const hydrateRelations = (relsResponse) => {
        relsResponse.forEach((row) => {
            let relation = {};
            if (row["kind"]) {
                row["kind"] =
                    `${row.kind}` + `${row.name ? " (" + row.name + ")" : ""}`;
            }
            if (row["to_asset_id"]) {
                if (row["to_asset_id"] === asset.id) {
                    relation = relatedAssets.find(
                        (storedAsset) =>
                            storedAsset.id === row["from_asset_id"],
                    );
                } else {
                    relation = relatedAssets.find(
                        (storedAsset) => storedAsset.id === row["to_asset_id"],
                    );
                }
                row["to_asset_id"] =
                    `<a href="/asset/${relation?.kind}/${encodeURIComponent(relation?.name || "")}">${relation?.nickname || relation?.name || "Unknown"}</a> <span>(${relation?.kind || "N/A"})</span>`;
            }
            if (row["from_asset_id"]) {
                if (row["from_asset_id"] === asset.id) {
                    row["from_asset_id"] = `<span>${$format("to")}</span>`;
                } else {
                    row["from_asset_id"] = `<span>${$format("from")}</span>`;
                }
            }
            row["finding_count"] = powerTableService.parseFindingCountRow(
                relation || {},
            );
        });
        relations = relsResponse;
    };

    const getData = async (urlParams = {}) => {
        if (!urlParams.id || !token.access_token) return;
        is_loading = true;
        is_loading_asset = true;
        is_loading_props = true;
        is_loading_rels = true;
        is_loading_findings = true;
        is_loading_tags = true;

        let decodedId = decodeURIComponent(urlParams.id);
        let assetResponse = urlParams.id
            ? await getAsset(decodedId, urlParams.kind)
            : {};

        propsResponse = assetResponse.id
            ? await getProperties({ filter: { asset_id: assetResponse.id } })
            : [];
        let relsResponseFromId = assetResponse.id
            ? await getRelations({
                  filter: { from_asset_id: assetResponse.id },
              })
            : [];
        let relsResponseToId = assetResponse.id
            ? await getRelations({ filter: { to_asset_id: assetResponse.id } })
            : [];
        let findingsResponse = assetResponse.id
            ? await getFindings({ filter: { asset_id: assetResponse.id } })
            : [];

        let vulnerabilities = await vulnerabilitiesApi.fetchVulnerabilities(
            "v2/vulnerability/",
            {filter: {asset__id: decodedId}},
        );
        let relsResponse = [...relsResponseFromId, ...relsResponseToId];
        let breachDateResponse =
            assetResponse.kind === "email"
                ? await searchProperties({ filter: { name: "BreachDate" } })
                : [];

        asset = assetResponse;
        is_loading_asset = false;

        for (let prop of propsResponse) {
            if (prop.location === "") {
                prop.location = "General";
            }
        }
        properties = propsResponse;
        is_loading_props = false;

        if (assetResponse.id) {
            relatedAssets = await getRelatedAssets(assetResponse.id);
            hydrateRelations(relsResponse);
        }
        is_loading_rels = false;

        let tagsData = assetResponse.id
            ? await getTags()
            : { tags: [], associations: [], allTags: [] };
        is_loading_tags = false;

        for (let fnd of findingsResponse) {
            let vulnerability =
                vulnerabilities.find(
                    (vul) => vul.id === fnd.vulnerability_id,
                ) || {};
            if (vulnerability.level === "critical") fnd.order = 1000;
            else if (vulnerability.level === "high") fnd.order = 100;
            else if (vulnerability.level === "medium") fnd.order = 10;
            else if (vulnerability.level === "low") fnd.order = 1;
            else fnd.order = 0;

            let tag_ids = [];
            if (tagsData && tagsData.associations) {
                for (let association of tagsData.associations) {
                    if (
                        association.target_object_type === "Finding" &&
                        association.target_object_id === fnd.id
                    ) {
                        tag_ids = [...tag_ids, association.tag_id];
                    }
                }
            }
            fnd.tag = tagsData.allTags
                ? tagsData.allTags.filter((tag) => tag_ids.includes(tag.id))
                : [];
            fnd.asset = assetResponse;
            fnd.vulnerability =
                $locale === "es"
                    ? vulnerability.title_es || vulnerability.id
                    : vulnerability.title || vulnerability.id;
            fnd.title = vulnerability.title;
            fnd.priority = vulnerability.priority;
            fnd.level = vulnerability.level;

            if (breachDateResponse.length > 0) {
                for (let bDate of breachDateResponse) {
                    if (bDate.vulnerability_id === fnd.vulnerability_id) {
                        fnd.breachDate = bDate.value;
                    }
                }
            }
        }

        findingsResponse.sort((a, b) => b.order - a.order);
        findings = findingsResponse;
        is_loading_findings = false;
        is_loading = false;
    };

    const showRows = () => {
        table_is_expanded = !table_is_expanded;
    };

    const toggleCollapsibleItems = (clicked) => {
        if (clicked === "properties") {
            showProperties = !showProperties;
            showSettings = showProperties ? showSettings : false;
        } else {
            showSettings = !showSettings;
            showProperties = showSettings ? true : showProperties;
        }
    };

    const handleTagsReset = () => {
        showTagsForm = false;
        showTagsDeleteForm = false;
        tagRegexError = null;
    };

    const dropdownAction = (node) => {
        const bs =
            typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        let inst;
        if (bs) {
            // Initialize the dropdown explicitly
            inst = new bs.Dropdown(node);
        }
        return {
            destroy() {
                if (inst) inst.dispose();
            },
        };
    };

    // Add this state variable near your other $state variables
    let isDropdownOpen = $state(false);

    // Svelte action to detect clicks outside the dropdown
    const clickOutside = (node, callback) => {
        const handleClick = (event) => {
            if (
                node &&
                !node.contains(event.target) &&
                !event.defaultPrevented
            ) {
                callback();
            }
        };
        // Use capture phase (true) for reliable triggering
        document.addEventListener("click", handleClick, true);
        return {
            destroy() {
                document.removeEventListener("click", handleClick, true);
            },
        };
    };

    const getLog = async () => {
        let logs = asset.id
            ? await logService.search({ filter: { search: asset.id } })
            : [];
        logs.forEach((log) => {
            let timestamp = dayjs(log.timestamp).format().split("T");
            let time = timestamp[1].split("-");
            log.date = dayjs(timestamp[0]).format("YYYY-MM-DD");
            log.time = time[0];

            if (log.target_object_name && log.target_object_name.length > 40) {
                log.long_target_object_name = log.target_object_name.split(",");
            }
        });
        return parseLogsFunc(logs);
    };

    const parseLogsFunc = (pageContent) => {
        pageContent.forEach((row) => {
            if (row["target_object_type"]) {
                row["target_object_type"] =
                    translateEventId(row.target_object_type) ||
                    row.target_object_type;
            }
            if (row["source_user_name"]) {
                row["source_user_name"] = row.source_user_name.includes(
                    "@internal.vulseek.io",
                )
                    ? $format("page.log.system_user")
                    : row.source_user_name;
            }
            if (row["event_id"]) {
                row["event_id"] =
                    translateEventId(row.event_id) || row.event_id;
            }
            if (row["target_object_name"]) {
                if (
                    !row["target_object_name"].includes("cpe") &&
                    row["target_object_name"].includes(":")
                ) {
                    let long_name = row.target_object_name.split(":");
                    row.target_object_name =
                        long_name[0] + " - " + long_name[1];
                }
            }
            if (row["event_data"]) {
                if (
                    row["event_data"].method &&
                    row["event_data"].method === "password"
                ) {
                    row["event_data"] = "Contraseña";
                } else if (row["event_data"].key && !row["event_data"].method) {
                    row["event_data"] = "";
                } else if (
                    row["event_data"].to_asset_id &&
                    row["event_data"].from_asset_id
                ) {
                    row.event_data = "Activo relacionado";
                } else if (row["event_data"].status) {
                    let new_status = row.event_data.status.split(".")[1];
                    row["event_data"] =
                        `${$format("page.log.status_change")} ${$format(`finding.status.${new_status}`)}`;
                } else if (row["event_data"].in_scope) {
                    row["event_data"] = $format(
                        `page.log.scope_${row.event_data.in_scope}`,
                    );
                } else {
                    let eventKeys = Object.keys(row["event_data"]);
                    if (row.event_id && row.event_id === "Creación") {
                        row.event_data = row["event_data"].name
                            ? "Nombre: " + row["event_data"].name
                            : "";
                    } else {
                        row["event_data"] = eventKeys.join(", ");
                    }
                }
            }
        });
        return pageContent;
    };

    const handleFindingsTableRefresh = async () => {
        if (asset?.id) {
            is_loading_findings = true;
            let findingsResponse = await getFindings({
                filter: { asset_id: asset.id },
            });
            let vulnerabilities = await vulnerabilitiesApi.fetchVulnerabilities(
                "v2/vulnerability/",
                {},
            );
            let tagsData = await getTags();

            for (let fnd of findingsResponse) {
                let vulnerability =
                    vulnerabilities.find(
                        (vul) => vul.id === fnd.vulnerability_id,
                    ) || {};
                if (vulnerability.level === "critical") fnd.order = 1000;
                else if (vulnerability.level === "high") fnd.order = 100;
                else if (vulnerability.level === "medium") fnd.order = 10;
                else if (vulnerability.level === "low") fnd.order = 1;
                else fnd.order = 0;

                let tag_ids = [];
                for (let association of tagsData.associations) {
                    if (
                        association.target_object_type === "Finding" &&
                        association.target_object_id === fnd.id
                    ) {
                        tag_ids = [...tag_ids, association.tag_id];
                    }
                }
                fnd.tag = tagsData.allTags.filter((tag) =>
                    tag_ids.includes(tag.id),
                );
                fnd.asset = asset;
                fnd.vulnerability =
                    vulnerability.title_es || vulnerability.title;
                fnd.priority = vulnerability.priority;
                fnd.level = vulnerability.level;
            }

            findingsResponse.sort((a, b) => b.order - a.order);
            findings = findingsResponse;
            is_loading_findings = false;
        }
    };

    const translateEventId = (value) => {
        if ($locale !== "es" || !value) return value || "";

        const labels = {
            finding: "Hallazgo",
            service: "Servicio",
            certificate: "Certificado",
            product: "Producto",
            user: "Usuario",
            network: "Red",
            domain: "Dominio",
            cloud: "Cuenta cloud",
            ip: "IP",
            Document: "Documento",
            document: "Documento",
            "asset.public_ip": "IP",
            "asset.private_ip": "IP",
            "asset.public_network": "Red",
            "asset.private_network": "Red",
            "asset.finding": "Hallazgo",
            "asset.certificate": "Certificado",
            "asset.domain": "Dominio",
            "asset.ad": "Active Directory",
            "asset.software": "Código fuente",
            "asset.endpoint": "Endpoint",
            "asset.other": "Otros activos",
            "asset.cloud": "Cuenta Cloud",
            "asset.hostname": "Hostname",
        };

        let regex = new RegExp(value, "gi");

        if (value.includes("token.create")) value = "Inicio de sesión";
        else if (value.includes("token.delete")) value = "Cierre de sesión";
        else if (value.includes("password"))
            value = value.replace("password", "Contraseña");
        else if (value.includes("otac")) value = value.replace("otac", "OTAC");
        else if (value.includes(".create")) value = "Creación";
        else if (value.includes(".delete")) value = "Eliminación";
        else if (value.includes("document.processed"))
            value = "Documento procesado";
        else if (value.includes("finding.open")) value = "Hallazgo abierto";
        else if (value.includes("update")) value = "Actualización";
        else if (value.includes("Property")) value = "Propiedad";
        else if (value.match(regex))
            value = value.replace(regex, labels[value.toLowerCase()] || value);

        if (value === "undefined") value = "";
        return value;
    };

    const logColumnsDef = [
        { key: "date", title: $format("list.columns.date"), filterable: false },
        { key: "time", title: $format("list.columns.hour"), filterable: false },
        {
            key: "target_object_type",
            title: $format("list.columns.type"),
            filterable: false,
        },
        {
            key: "source_user_name",
            title: $format("list.columns.user"),
            filterable: false,
        },
        {
            key: "event_id",
            title: $format("list.columns.event"),
            filterable: false,
        },
        {
            key: "target_object_name",
            title: $format("list.columns.data"),
            filterable: false,
        },
        {
            key: "event_data",
            title: $format("list.columns.resource"),
            filterable: false,
        },
    ];

    $effect(() => {
        const currentKind = $params?.kind;
        const currentId = $params?.id;
        if (currentKind && currentId) {
            untrack(() => {
                getData({ kind: currentKind, id: currentId });
            });
        }
    });
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <h1 class="mt-3 mb-3 page-title">{asset.nickname || asset.name || ""}</h1>
            </div>
            <div class="col-12 mt-3 breadcrumb">
                {#if asset.kind}
                    <!-- CRITICAL FIX: Square brackets strictly preserved; do NOT concatenate asset.kind into path! -->
                    <BreadCrumNav
                        routes={[
                            {
                                path: "/asset/[kind]",
                                params: { kind: asset.kind },
                                label: asset.kind,
                            },
                            { label: asset.name },
                        ]}
                    />
                {/if}
            </div>
        </div>
    </div>
</section>
<section class="content mt-3">
    <div class="container-fluid">
        <SeverityTotals {totals} totals_by_status={null} />
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12 d-flex" style="gap: 15px;">
                <div class="card mt-3" style="flex: 1;">
                    <div class="card-header" style="min-height: 62px;">
                        <div class="card-title">{$format("data")}</div>
                        <div class="card-tools">
                            <button
                                title={$format("see_log")}
                                class="btn btn-secondary table-button log-btn"
                                onclick={openLogModal}
                            >
                                <i class="fas fa-align-justify"></i>
                                {$format("see_log")}
                            </button>
                        </div>
                    </div>
                    <div class="card-body">
                        {#if !is_loading_asset}
                            <AssetDetail {asset} />
                        {:else}
                            <Spinner />
                        {/if}
                    </div>
                </div>
                <div class="card mt-3" style="flex: 1;">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.asset.asset_tags")}
                        </div>
                        {#if user.data.role !== "viewer"}
                            <div class="card-tools">
                                {#if !showTagsForm && !showTagsDeleteForm}
                                    <!-- SVELTE-NATIVE DROPDOWN FIX -->
                                    <div
                                        class="dropdown d-inline-block"
                                        use:clickOutside={() =>
                                            (isDropdownOpen = false)}
                                    >
                                        <button
                                            type="button"
                                            title={$format("buttons.add")}
                                            aria-label={$format("buttons.add")}
                                            class="btn btn-secondary table-button btn-add btn-xs dropdown-toggle"
                                            onclick={() =>
                                                (isDropdownOpen =
                                                    !isDropdownOpen)}
                                            aria-expanded={isDropdownOpen}
                                        >
                                            <i class="fas fa-plus"></i>
                                        </button>

                                        <!-- class:show dynamically applies Bootstrap's show class -->
                                        <!-- dropdown-menu-end aligns it correctly to the right -->
                                        <ul
                                            class="dropdown-menu dropdown-menu-end"
                                            class:show={isDropdownOpen}
                                            style="position: absolute; right: 0; left: auto; margin-top: 2px;"
                                        >
                                            <li>
                                                <a
                                                    class="dropdown-item"
                                                    href="#"
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        isDropdownOpen = false; // Close menu on click
                                                        showTagsForm = true;
                                                    }}
                                                >
                                                    {$format(
                                                        "buttons.select_tag.new",
                                                    )}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    class="dropdown-item"
                                                    href="#"
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        isDropdownOpen = false; // Close menu on click
                                                        openTagModal(e);
                                                    }}
                                                >
                                                    {$format(
                                                        "buttons.select_tag.add_and_associate",
                                                    )}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>

                                    <button
                                        title={$format("buttons.delete")}
                                        aria-label={$format("buttons.delete")}
                                        class="btn btn-secondary table-button btn-delete btn-xs"
                                        onclick={() =>
                                            (showTagsDeleteForm = true)}
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                {:else}
                                    <button
                                        title={$format("buttons.back")}
                                        aria-label={$format("buttons.back")}
                                        class="btn btn-xs"
                                        onclick={() => handleTagsReset()}
                                    >
                                        <i class="fas fa-backward"></i>
                                    </button>
                                {/if}
                            </div>
                        {/if}
                    </div>
                    <div class="card-body mt-3">
                        {#key refresh_tag_comp}
                            {#if is_loading_tags}
                                <Spinner />
                            {:else}
                                <Tag
                                    {formData}
                                    {tags}
                                    {allTags}
                                    {showTagsForm}
                                    {showTagsDeleteForm}
                                    on:submitTags={handleTagEdit}
                                    on:deleteTags={handleTagDelete}
                                    on:cancel={handleTagsReset}
                                />
                            {/if}
                        {/key}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">{$format("properties")}</div>
                        <div class="float-end">
                            <button
                                class="btn btn-default btn-collapse float-start"
                                type="button"
                                aria-label="toggleCollapsibleItems"
                                onclick={() =>
                                    toggleCollapsibleItems("settings")}
                            >
                                <i class="fas fa-cog"></i>
                            </button>
                            {#if showSettings}
                                <div
                                    class="float-start"
                                    transition:fade={{ delay: 0, duration: 50 }}
                                >
                                    <div
                                        class="dropdown-menu dropdown-menu-xl dropdown-menu-right show"
                                        style="left: inherit; right: 0px;"
                                    >
                                        <a
                                            href="#"
                                            class="dropdown-item"
                                            class:is-selected={props_grouped_by ===
                                                "knv"}
                                            disabled={props_grouped_by ===
                                                "knv"}
                                            onclick={() =>
                                                groupPropertiesByKNV()}
                                        >
                                            <i class="fas fa-object-group me-1"
                                            ></i>{$format(
                                                "page.properties.group_name_kind_value",
                                            )}
                                        </a>
                                        <div class="dropdown-divider"></div>
                                        <a
                                            href="#"
                                            class="dropdown-item"
                                            class:is-selected={props_grouped_by ===
                                                "none"}
                                            disabled={props_grouped_by ===
                                                "none"}
                                            onclick={() => ungroupProperties()}
                                        >
                                            <i
                                                class="fas fa-object-ungroup me-1"
                                            ></i>{$format(
                                                "page.properties.ungroup",
                                            )}
                                        </a>
                                    </div>
                                </div>
                            {/if}
                            <button
                                class="btn btn-default btn-collapse float-end"
                                type="button"
                                onclick={() =>
                                    toggleCollapsibleItems("properties")}
                            >
                                <i
                                    class={!showProperties
                                        ? "fas fa-angle-down"
                                        : "fas fa-angle-up"}
                                ></i>
                            </button>
                        </div>
                    </div>
                    {#if showProperties}
                        <div
                            class="card-body"
                            transition:fade|global={{ delay: 0, duration: 200 }}
                        >
                            <div class="table-responsive table-readOnly">
                                {#if !is_loading_props}
                                    <Properties
                                        ptInstructs={propInstructs}
                                        {properties}
                                    />
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.asset.relations")}
                        </div>
                        <div class="float-end">
                            <button
                                class="btn btn-default btn-collapse float-end"
                                type="button"
                                onclick={() => (showRelations = !showRelations)}
                            >
                                <i
                                    class={!showRelations
                                        ? "fas fa-angle-down"
                                        : "fas fa-angle-up"}
                                ></i>
                            </button>
                        </div>
                    </div>
                    {#if showRelations}
                        <div
                            class="card-body"
                            transition:fade|global={{ delay: 0, duration: 200 }}
                        >
                            <div class="table-responsive table-readOnly">
                                {#if !is_loading_rels}
                                    <AssetRelations rels={relations} />
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</section>
<section class="content">
    <div class="container-fluid">
        <div>
            {#if !is_loading_findings}
                {#key table_is_expanded}
                    <Finding
                        tags={allTags}
                        {table_is_expanded}
                        parent={asset.kind === "email" ? "email" : "asset"}
                        showRefresh={true}
                        show_expand_button={true}
                        refreshFunc={handleFindingsTableRefresh}
                        on:showRows={showRows}
                    />
                {/key}
            {:else}
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">Hallazgos</div>
                    </div>
                    <div class="card-body">
                        <Spinner />
                    </div>
                </div>
            {/if}
        </div>
    </div>
</section>

<!-- TAG MODAL -->
<div class="modal fade" id="tag-add-new-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("page.tag.add")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={closeTagModal}
                ></button>
            </div>
            <div class="modal-body">
                <TagForm
                    on:save={addNewTag}
                    on:closeModal={closeTagModal}
                    tag={null}
                />
            </div>
        </div>
    </div>
</div>

<!-- LOGS MODAL -->
{#if loadLogData}
    <div class="modal fade" id="get-log-modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog" style="max-width: 80%; width: 80%;">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Logs</h4>
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        onclick={() => logModal && logModal.hide()}
                    ></button>
                </div>
                <div class="modal-body">
                    {#await getLog()}
                        <div class="card mt-3">
                            <div class="card-header"></div>
                            <div class="card-body">
                                <Spinner />
                            </div>
                        </div>
                    {:then log}
                        {#if !is_loading_asset}
                            <LogList
                                data={log}
                                instructs={logColumnsDef}
                                parseFunc={parseLogsFunc}
                                prefix={"logsTable"}
                            />
                        {/if}
                    {/await}
                </div>
            </div>
        </div>
    </div>
{/if}

<style>
    .breadcrumb {
        padding: 0 15px !important;
    }
    .btn-collapse {
        border: none;
        background-color: transparent;
    }
    .log-btn {
        height: 27px !important;
        width: auto !important;
        max-width: 100px !important;
        font-size: 0.75em;
        padding-right: 5px !important;
        padding-left: 5px !important;
    }
    .dropdown-toggle:after {
        content: none !important;
    }
    .btn-secondary.dropdown-toggle {
        background-color: rgba(248, 249, 250, 0.85) !important;
    }
    .btn-secondary:not(:disabled):not(.disabled):active,
    .btn-secondary:not(:disabled):not(.disabled).active,
    .show > .btn-secondary.dropdown-toggle {
        background-color: rgba(248, 249, 250, 0.85) !important;
    }
    .is-selected {
        background-color: #f8f9fa !important;
        color: #0056b3 !important;
    }
</style>
